import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../layout/SiteHeader';
import PageBreadcrumb from '../layout/PageBreadcrumb';
import SiteLayout, { SiteMain } from '../layout/SiteLayout';
import { CONTACT_PATH, SUPPORT_EMAIL } from '../../constants/siteNav';

/**
 * Shared shell for Terms / Privacy - cream theme, readable body type, soft sections.
 */
const LegalDocumentLayout = ({ title, subtitle, children }) => (
  <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50/80 to-amber-50/90">
    <SiteHeader />

    <SiteMain>
      <PageBreadcrumb items={[{ label: 'Home', to: '/' }, { label: title }]} />

      {/* Soft hero */}
      <section className="relative overflow-hidden border-b border-amber-100/80">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          aria-hidden
          style={{
            backgroundImage:
              'radial-gradient(circle at 12% 20%, rgba(251,146,60,0.18), transparent 42%), radial-gradient(circle at 88% 10%, rgba(245,158,11,0.12), transparent 38%)'
          }}
        />
        <div className="relative container mx-auto px-4 pt-10 pb-14 md:pt-14 md:pb-16">
          <div className="max-w-3xl mx-auto">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 tracking-tight leading-tight mb-3">
              {title}
            </h1>
            {subtitle ? (
              <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {/* Document body */}
      <section className="relative z-10 px-4 -mt-6 md:-mt-8 pb-16">
        <div className="container mx-auto max-w-3xl">
          <article className="bg-white/95 backdrop-blur-sm rounded-xl shadow-[0_8px_40px_rgba(180,83,9,0.08)] border border-amber-100/70 overflow-hidden">
            <div className="legal-prose px-6 py-8 md:px-10 md:py-11 space-y-10 text-[15px] md:text-base text-gray-600 leading-[1.75] font-sans">
              {children}
            </div>

            <div className="px-6 md:px-10 py-6 bg-gradient-to-r from-amber-50/90 to-orange-50/70 border-t border-amber-100/80">
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Questions about these terms? We&apos;re happy to help.
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="inline-flex items-center gap-2 font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                >
                  <i className="fas fa-envelope text-xs" aria-hidden />
                  {SUPPORT_EMAIL}
                </a>
                <span className="text-amber-200" aria-hidden>
                  ·
                </span>
                <Link
                  to={CONTACT_PATH}
                  className="inline-flex items-center gap-2 font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Contact support
                  <i className="fas fa-arrow-right text-[10px]" aria-hidden />
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </SiteMain>
  </SiteLayout>
);

export const LegalSection = ({ title, children }) => (
  <section className="scroll-mt-24">
    <h2 className="font-display text-lg md:text-[1.2rem] font-bold text-gray-900 tracking-tight mb-4 pb-2 border-b border-amber-100/90">
      {title}
    </h2>
    <div className="space-y-4 [&_p]:text-gray-600 [&_p]:leading-[1.75] [&_strong]:text-gray-800 [&_strong]:font-semibold">
      {children}
    </div>
  </section>
);

export const LegalSubheading = ({ children }) => (
  <p className="text-sm font-semibold text-gray-800 tracking-wide mt-2 mb-1 first:mt-0">
    {children}
  </p>
);

/** Soft checklist-style bullets that fit the cream theme */
export const LegalList = ({ children }) => (
  <ul className="my-1 space-y-0 rounded-lg border border-amber-100/80 bg-amber-50/40 overflow-hidden">
    {React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) return child;
      return React.cloneElement(child, { index });
    })}
  </ul>
);

export const LegalListItem = ({ children, index = 0 }) => (
  <li
    className={`flex gap-3 px-3.5 py-3 text-[15px] md:text-[0.95rem] text-gray-600 leading-relaxed ${
      index > 0 ? 'border-t border-amber-100/70' : ''
    }`}
  >
    <span
      className="mt-2 h-1.5 w-1.5 rounded-full bg-orange-400 shrink-0 shadow-[0_0_0_3px_rgba(251,146,60,0.2)]"
      aria-hidden
    />
    <span className="min-w-0 flex-1 [&_strong]:text-gray-800 [&_strong]:font-semibold">{children}</span>
  </li>
);

export default LegalDocumentLayout;
